import {
  Box,
  FormControl,
  FormLabel,
  Icon,
  Image,
  Progress,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdUpload } from "react-icons/md";
import { useDispatch } from "react-redux";
import { path } from "../../../utils/Api";
import { actions } from "./redux/slice";
import FileSizeExtension from "../../hooks/FileSizeExtension";
import { BsUpload } from "react-icons/bs";

interface Props {
  file: string;
  title: string;
  isRequiredFalse?: boolean;
  handleUploadImage?: (url: string, name?: string) => void;
  iaExcelUpload?: React.ReactNode;
}

const SingleFileUpload: React.FC<Props> = ({
  title,
  isRequiredFalse,
  file,
  handleUploadImage,
  iaExcelUpload,
}) => {
  const { t } = useTranslation();
  const bg = useColorModeValue("gray.100", "navy.700");
  const borderColor = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  const brandColor = useColorModeValue("brand.500", "white");
  const textColor = useColorModeValue("navy.700", "white");
  const [uploadFiles, setUploadFiles] = useState<any[]>([]);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const dispatch = useDispatch();

  const processFileUpload = async (files: FileList) => {
    const fileList = [...uploadFiles];

    for (let file of Array.from(files)) {
      if (!FileSizeExtension(file, iaExcelUpload ? "pdf" : "photo")) continue;

      const newEntry = {
        name: file.name,
        url: "",
        prefix: "",
        data: file,
        inprogress: false,
        completed: false,
      };

      fileList.push(newEntry);
    }

    setUploadFiles([...fileList]);

    for (let i = 0; i < fileList.length; i++) {
      const entry = fileList[i];
      if (!entry.completed && !entry.inprogress) {
        const { url, prefix } = await getSignedUrl(entry.name);
        fileList[i].url = url;
        fileList[i].prefix = prefix;
      }
    }

    setUploadFiles([...fileList]);

    for (let i = 0; i < fileList.length; i++) {
      const entry = fileList[i];
      if (!entry.completed && !entry.inprogress) {
        fileList[i].inprogress = true;
        setUploadFiles([...fileList]);
        setProgress(0);
        await uploadFile(entry.url, entry.data);
        fileList[i].completed = true;
        fileList[i].inprogress = false;
        setUploadFiles([...fileList]);
        if (handleUploadImage)
          handleUploadImage(fileList[i].prefix, fileList[i].name);
      }
    }
  };

  const handleFileChange = async (evt: React.ChangeEvent<HTMLInputElement>) => {
    if (evt.target.files) {
      await processFileUpload(evt.target.files);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files) {
      await processFileUpload(e.dataTransfer.files);
    }
  };

  const getSignedUrl = (
    fileName: string
  ): Promise<{ url: string; prefix: string }> => {
    return new Promise((resolve, reject) => {
      try {
        dispatch(
          actions.getSignedUrl({
            fileName,
            callback: (url: string, prefix: string) => {
              resolve({ url, prefix });
            },
          })
        );
      } catch (error) {
        reject(error);
      }
    });
  };

  const uploadFile = (signedUrl: string, file: File) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("Content-Type", "application/octet-stream");

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          const percent = (evt.loaded / evt.total) * 100;
          setProgress(percent);
        }
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error("Failed to upload file"));
          }
        }
      };

      xhr.send(file);
    });
  };

  return (
    <Stack direction="column">
      <FormControl isRequired={!isRequiredFalse}>
        {title ?? (
          <FormLabel fontSize="sm" fontWeight="500" color={textColor} mb="8px">
            {title}
          </FormLabel>
        )}

        {iaExcelUpload ? (
          <FormLabel
            htmlFor={`id-${title}`}
            bg={brandColor}
            borderRadius="16px"
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minW={160}
          >
            <Box
              alignItems="center"
              justifyContent="center"
              display="flex"
              px={"25px"}
              py={2}
              gap={4}
            >
              <Icon as={BsUpload} w="15px" h="15px" color="#fff" mx="auto" />
              <Text fontSize="sm" fontWeight="500" color="#fff">
                {t("order.list.uploadExcel")}
              </Text>
            </Box>
          </FormLabel>
        ) : (
          <FormLabel
            htmlFor={`id-${title}`}
            onDragEnter={() => setDragActive(true)}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
            border="1px dashed"
            borderColor={dragActive ? brandColor : borderColor}
            bg={dragActive ? `${brandColor}33` : bg}
            borderRadius="16px"
            w="100%"
            cursor="pointer"
            minH="120px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box textAlign="center">
              <Icon
                as={MdUpload}
                w="40px"
                h="40px"
                color={brandColor}
                mx="auto"
              />
              <Text fontSize="md" fontWeight="700" color={brandColor}>
                {dragActive
                  ? t("common.form.dropHere") || "Drop files here"
                  : t("common.form.uploadFile")}
              </Text>
              <Text fontSize="sm" fontWeight="500" color="secondaryGray.500">
                {t("common.form.fileAllowed")}
              </Text>
            </Box>
          </FormLabel>
        )}

        <input
          type="file"
          accept=".jpg,.png,.jpeg,.xlsx,.xls"
          id={`id-${title}`}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </FormControl>

      {uploadFiles.filter((x) => x.inprogress).length > 0 && (
        <Box my={5}>
          <Text>{uploadFiles.find((x) => x.inprogress)?.name}</Text>
          <Progress colorScheme="brand" h="8px" value={progress} />
        </Box>
      )}

      {file?.length > 0 && (
        <Box mt={4} w="50px" h="50px">
          <Image
            src={path() + file}
            alt="uploaded"
            w="50px"
            h="50px"
            objectFit="cover"
            borderRadius="md"
          />
        </Box>
      )}
    </Stack>
  );
};

export default SingleFileUpload;
